import { Box, Breadcrumbs, Typography } from '@mui/material';
import { Home, ChevronRight } from '@mui/icons-material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useBreadcrumbConfig } from './BreadcrumbContext';

interface BreadcrumbProps {
  showHome?: boolean;
  showIcons?: boolean;
  maxItems?: number;
}

export function Breadcrumb({ showHome = true, showIcons = false, maxItems = 6 }: BreadcrumbProps) {
  const location = useLocation();
  const config = useBreadcrumbConfig();
  const pathSegments = location.pathname.split('/').filter(Boolean);

  // Build breadcrumb items from config or generate from path
  const items = buildBreadcrumbs(pathSegments, config, showHome, maxItems);

  if (items.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        px: 2,
        py: 1.5,
        bgcolor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Breadcrumbs
        separator={<ChevronRight sx={{ fontSize: 18, color: 'grey.400' }} />}
        sx={{ flex: 1 }}
      >
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          const isClickable = !!item.path && !isLast;

          return isClickable ? (
            <RouterLink
              key={item.label}
              to={item.path!}
              style={{
                color: 'inherit',
                textDecoration: 'none',
                fontSize: '0.875rem',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              {item.icon && showIcons && item.icon}
              {item.label}
            </RouterLink>
          ) : (
            <Typography
              key={item.label}
              variant="body2"
              sx={{
                color: isLast ? 'text.primary' : 'text.secondary',
                fontWeight: isLast ? 600 : 400,
                fontSize: '0.875rem',
              }}
            >
              {item.label}
            </Typography>
          );
        })}
      </Breadcrumbs>
    </Box>
  );
}

interface BreadcrumbItem {
  label: string;
  path?: string | null;
  icon?: React.ReactNode;
}

function buildBreadcrumbs(
  pathSegments: string[],
  config: ReturnType<typeof useBreadcrumbConfig>,
  showHome: boolean,
  maxItems: number
): BreadcrumbItem[] {
  // If we have custom config, use it
  if (config && config.length > 0) {
    return config;
  }

  // Otherwise generate from path
  const items: BreadcrumbItem[] = [];

  if (showHome) {
    items.push({ label: 'Home', path: '/', icon: <Home sx={{ fontSize: 16 }} /> });
  }

  // Build path incrementally
  let currentPath = '';
  for (let i = 0; i < pathSegments.length; i++) {
    currentPath += `/${pathSegments[i]}`;
    const label = formatLabel(pathSegments[i]);

    // Last segment is not clickable (current page)
    const isLast = i === pathSegments.length - 1;
    items.push({
      label,
      path: isLast ? null : currentPath,
    });
  }

  // Collapse if exceeds maxItems
  if (items.length > maxItems) {
    const collapsed: BreadcrumbItem[] = [items[0]];
    const ellipsis: BreadcrumbItem = { label: '...', path: null };
    const lastFew = items.slice(-(maxItems - 1));
    return [...collapsed, ellipsis, ...lastFew];
  }

  return items;
}

function formatLabel(segment: string): string {
  // Handle UUIDs - show truncated version
  if (segment.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
    return segment.slice(0, 8) + '...';
  }

  // Handle numeric IDs - show as 'Record #id'
  if (segment.match(/^\d+$/)) {
    return `#${segment}`;
  }

  // Convert kebab-case and snake_case to Title Case
  return segment
    .split(/[-_]/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}